import { Controller } from "@hotwired/stimulus"

const EARTH_KM = 6371

export default class extends Controller {
  static targets = [
    "list",
    "item",
    "selectedName",
    "selectedAddress",
    "selectedCity",
    "selectedPhone",
    "selectedDistance",
    "distanceBadge",
    "mapFrame",
    "mapPlaceholder",
    "mapsLink",
    "status",
    "locateButton"
  ]

  static values = {
    locations: Array,
    selectedId: Number,
    unit: { type: String, default: "km" }
  }

  connect() {
    this.userLat = null
    this.userLng = null
    this.onLocated = (e) => this.applyUserLocation(e.detail.lat, e.detail.lng)
    this.onDenied = () => this.showStatus(this.statusDeniedText())
    document.addEventListener("geo:located", this.onLocated)
    document.addEventListener("geo:denied", this.onDenied)

    if (!this.hasSelectedIdValue && this.locationsValue.length) {
      const primary = this.locationsValue.find((l) => l.primary) || this.locationsValue[0]
      this.selectedIdValue = primary?.id || 0
    }

    this.render()
  }

  disconnect() {
    document.removeEventListener("geo:located", this.onLocated)
    document.removeEventListener("geo:denied", this.onDenied)
  }

  select(event) {
    const id = Number(event.currentTarget.dataset.locationId)
    if (!Number.isFinite(id)) return
    this.selectedIdValue = id
    this.render()
  }

  applyUserLocation(lat, lng) {
    this.userLat = lat
    this.userLng = lng
    const nearest = this.sortedLocations()[0]
    if (nearest?.id) this.selectedIdValue = nearest.id
    this.render()
    this.showStatus(this.statusLocatedText())
  }

  sortedLocations() {
    return [...this.locationsValue]
      .map((loc) => ({ ...loc, distanceKm: this.distanceKm(loc) }))
      .sort((a, b) => {
        if (a.distanceKm == null && b.distanceKm == null) {
          if (a.primary === b.primary) return 0
          return a.primary ? -1 : 1
        }
        if (a.distanceKm == null) return 1
        if (b.distanceKm == null) return -1
        return a.distanceKm - b.distanceKm
      })
  }

  distanceKm(loc) {
    if (this.userLat == null || this.userLng == null) return null
    if (loc.latitude == null || loc.longitude == null) return null
    return this.haversine(this.userLat, this.userLng, loc.latitude, loc.longitude)
  }

  haversine(lat1, lon1, lat2, lon2) {
    const toRad = (d) => (d * Math.PI) / 180
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return Math.round(EARTH_KM * c * 10) / 10
  }

  formatDistance(km) {
    if (km == null) return ""
    const sep = document.documentElement.lang === "en" ? "." : ","
    return `${String(km).replace(".", sep)} km`
  }

  selectedLocation() {
    const sorted = this.sortedLocations()
    return sorted.find((l) => Number(l.id) === Number(this.selectedIdValue)) || sorted[0]
  }

  render() {
    const sorted = this.sortedLocations()
    const selected = this.selectedLocation()
    if (!selected) return

    if (this.hasSelectedNameTarget) this.selectedNameTarget.textContent = selected.name || ""
    if (this.hasSelectedAddressTarget) this.selectedAddressTarget.textContent = selected.address || ""
    if (this.hasSelectedCityTarget) {
      const cityLine = [selected.city, selected.state].filter(Boolean).join(", ")
      this.selectedCityTarget.textContent = cityLine
    }
    if (this.hasSelectedPhoneTarget) {
      const phone = selected.phone || ""
      this.selectedPhoneTarget.href = phone ? `tel:${phone.replace(/\s/g, "")}` : "#"
      this.selectedPhoneTarget.classList.toggle("hidden", !phone)
      const label = this.selectedPhoneTarget.querySelector("[data-phone-label]")
      if (label) label.textContent = phone
    }

    const dist = this.formatDistance(selected.distanceKm)
    if (this.hasSelectedDistanceTarget) {
      this.selectedDistanceTarget.textContent = dist
      this.selectedDistanceTarget.classList.toggle("hidden", !dist)
    }
    if (this.hasDistanceBadgeTarget) {
      this.distanceBadgeTarget.textContent = dist
      this.distanceBadgeTarget.classList.toggle("hidden", !dist)
    }

    if (this.hasMapsLinkTarget && selected.maps_query) {
      this.mapsLinkTarget.href = `https://maps.google.com/?q=${encodeURIComponent(selected.maps_query)}`
    }

    if (this.hasMapFrameTarget && selected.maps_query) {
      const src = `https://maps.google.com/maps?q=${encodeURIComponent(selected.maps_query)}&z=15&output=embed`
      const lazyRoot = this.mapFrameTarget.closest("[data-controller~='lazy-map']")
      if (lazyRoot) {
        lazyRoot.setAttribute("data-lazy-map-src-value", src)
        const app = window.Stimulus
        const ctrl = app?.getControllerForElementAndIdentifier?.(lazyRoot, "lazy-map")
        if (ctrl?.srcValue !== undefined) {
          ctrl.srcValue = src
          ctrl.load?.(true)
        } else {
          this.mapFrameTarget.src = src
        }
      } else {
        this.mapFrameTarget.src = src
      }
    }

    if (this.hasItemTarget) {
      this.itemTargets.forEach((el) => {
        const id = Number(el.dataset.locationId)
        const loc = sorted.find((l) => Number(l.id) === id)
        const active = Number(selected.id) === id
        el.classList.toggle("clinic-location-option--active", active)
        el.setAttribute("aria-pressed", active ? "true" : "false")
        const badge = el.querySelector("[data-distance-label]")
        if (badge) {
          const label = loc ? this.formatDistance(loc.distanceKm) : ""
          badge.textContent = label
          badge.classList.toggle("hidden", !label)
        }
      })
    }
  }

  showStatus(text) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text || ""
    this.statusTarget.classList.toggle("hidden", !text)
  }

  statusLocatedText() {
    return this.element.dataset.locatedLabel || ""
  }

  statusDeniedText() {
    return this.element.dataset.deniedLabel || ""
  }
}
